import { type ReactNode } from 'react'
import { AlertErrorCircle } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'

/** Dismissible error banner shown above the blank-state card when a first build failed. */
export type MatterRecordEmptyStateError = {
  headline: string
  detail: string
  dismissLabel: string
  onDismiss: () => void
}

interface MatterRecordEmptyStateProps {
  illustration: ReactNode
  title: string
  body: string
  ctaLabel: string
  onCtaClick: () => void
  /** Disables the CTA — e.g. a read-only collaborator who can't initiate a build. */
  ctaDisabled?: boolean
  ctaLoading?: boolean
  /** Popover on the CTA, used to explain why it's disabled. */
  ctaTooltip?: string
  error?: MatterRecordEmptyStateError
  /** In-progress Add Files session notice shown above the card (e.g. "3 files uploading — continue in Add Files"). */
  notice?: string
}

/**
 * Blank state shown when the matter has no living asset yet (Figma: blank state). When a
 * first-generation attempt failed, the dismissible error banner from Figma "First generation - error"
 * (node 9095:366770) shows above the card — the user lands here after a failed Generate.
 *
 * Presentational: the feature supplies the illustration, copy, CTA, and (optional) error banner.
 */
export default function MatterRecordEmptyState(props: MatterRecordEmptyStateProps) {
  const { illustration, title, body, ctaLabel, onCtaClick, ctaDisabled = false, ctaLoading = false, ctaTooltip, error, notice } = props

  // A disabled CTA with an explanation stays focusable so keyboard users can open the popover.
  // aria-disabled and the omitted action preserve the disabled behavior without blocking the trigger click.
  const cta = (
    <Button
      onClick={ctaDisabled ? undefined : onCtaClick}
      aria-disabled={ctaDisabled || undefined}
      disabled={ctaDisabled && !ctaTooltip}
      loading={ctaLoading}
      size="sm"
      className={ctaDisabled && ctaTooltip ? '!cursor-not-allowed !bg-true-gray-300 !text-white' : undefined}
    >
      {ctaLabel}
    </Button>
  )

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Error banner */}
      {error && (
        <div className="p-4">
          <div className="flex flex-col sm:flex-row items-start md:items-center gap-2.5 pl-4 pr-2 py-2.5 bg-red-50 border border-brand-neutral-200 rounded-md">
            {/* Stacks below sm so the detail text keeps full width instead of wrapping in a sliver
                beside the nowrap headline. */}
            <div className="flex-1 min-w-0 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
              <span className="flex items-center gap-1 shrink-0">
                <AlertErrorCircle className="h-3.5 w-3.5 shrink-0 text-red-800" aria-hidden />
                <span className="text-xs font-medium text-red-800 whitespace-nowrap">{error.headline}</span>
              </span>
              <span className="text-xs font-medium text-brand-neutral-950">{error.detail}</span>
            </div>
            <Button onClick={error.onDismiss} variant="plain" color="secondary" size="xs" className="self-end sm:self-auto shrink-0">
              {error.dismissLabel}
            </Button>
          </div>
        </div>
      )}
      {/* Notice banner */}
      {notice && (
        <div className="p-4">
          <InlineMessage variant="info">{notice}</InlineMessage>
        </div>
      )}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
        <div className="w-full max-w-[930px] bg-white border border-brand-neutral-200 rounded-lg flex flex-col items-center gap-4 px-4 py-6">
          {/* Figma chronology blank state (9061:258421): outlined document + medical cross + clock disc. */}
          {illustration}
          <div className="flex flex-col items-center gap-0.5 text-center w-full max-w-[460px]">
            <p className="text-base font-medium text-brand-neutral-950 leading-6">{title}</p>
            <p className="text-sm font-normal text-brand-neutral-600 tracking-[-0.15px] leading-5">{body}</p>
          </div>
          {ctaTooltip ? (
            <Popover>
              <PopoverTrigger asChild>{cta}</PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="center"
                sideOffset={8}
                width="auto"
                showArrow
                showCloseButton={false}
                className="w-[260px] max-w-[calc(100vw-2rem)] font-sans text-sm tracking-[-0.15px] shadow-none [filter:drop-shadow(0px_0.6px_12.5px_rgba(0,0,0,0.10))_drop-shadow(0px_10px_5px_rgba(0,0,0,0.04))]"
              >
                <span className="text-sm font-normal text-brand-neutral-950 tracking-[-0.15px] leading-5">{ctaTooltip}</span>
              </PopoverContent>
            </Popover>
          ) : (
            cta
          )}
        </div>
      </div>
    </div>
  )
}
